<template>
  <div class="page-content">
    <breadcrumb-index 
      :items="breadcrumbs"
      icon="check-square"
      :badge="tests.length ? `${tests.length} tests` : null"
      badge-variant="badge-primary"
    >
      <template #actions>
        <button @click="openCreateModal" class="btn btn-primary btn-sm">
          <vue-feather type="plus" size="14" class="me-1" />
          Add Test
        </button>
      </template>
    </breadcrumb-index>

    <DataTable
      :items="tests"
      :columns="columns"
      :loading="loading"
      :searchable="false"
      empty-icon="check-square"
      empty-title="No tests found"
      empty-description="Create your first test to get started"
    >
      <template #cell-title="{ item }">
        <div class="avatar-info">
          <span class="avatar-name">{{ item.title }}</span>
          <span v-if="item.scheduledAt" class="avatar-subtitle">
            <vue-feather type="clock" size="12" class="me-1" />
            {{ formatDate(item.scheduledAt) }}
          </span>
        </div>
      </template>

      <template #cell-duration="{ item }">
        <span class="fw-medium">{{ item.durationMinutes || item.duration || item.duration_minutes }} min</span>
      </template>

      <template #cell-questions="{ item }">
        <div class="d-flex align-items-center gap-2">
          <router-link :to="`/app/tests/${item.id}/questions`" class="badge bg-primary text-decoration-none">
            {{ item.questionCount || item.totalQuestions || 0 }} questions
          </router-link>
          <button 
            v-if="!(item.questionCount || item.totalQuestions)" 
            class="btn btn-outline-primary btn-xs"
            @click="openPopulateModal(item)"
            v-tooltip="'Populate questions'"
          >
            <vue-feather type="cpu" size="12" />
          </button>
        </div>
      </template>

      <template #cell-marks="{ item }">
        <div class="avatar-info">
          <span class="avatar-name">{{ item.totalMarks || item.total_marks }}</span>
          <span class="avatar-subtitle">Pass: {{ item.passingMarks || item.passing_marks }}</span>
        </div>
      </template>

      <template #cell-status="{ item }">
        <span :class="['status-badge', getStatusClass(item.status)]">
          {{ item.status }}
        </span>
      </template>

      <template #cell-actions="{ item }">
        <div class="action-buttons">
          <router-link 
            :to="`/app/tests/${item.id}/questions`" 
            class="action-btn action-view" 
            v-tooltip="'View questions'"
          >
            <vue-feather type="help-circle" size="14" />
          </router-link>
          <router-link 
            :to="`/app/tests/${item.id}/results`" 
            class="action-btn" 
            v-tooltip="{ text: 'View results', variant: 'success' }"
            style="color: #10b981;"
          >
            <vue-feather type="bar-chart-2" size="14" />
          </router-link>
          <button 
            class="action-btn action-edit" 
            @click="openEditModal(item)"
            v-tooltip="'Edit test'"
          >
            <vue-feather type="edit-2" size="14" />
          </button>
          <button 
            class="action-btn action-delete" 
            @click="confirmDelete(item)" 
            v-tooltip="{ text: 'Delete test', variant: 'danger' }"
          >
            <vue-feather type="trash-2" size="14" />
          </button>
        </div>
      </template>

      <template #empty-action>
        <button @click="openCreateModal" class="btn btn-primary">
          <vue-feather type="plus" size="16" class="me-1" />
          Create First Test
        </button>
      </template>
    </DataTable>

    <!-- Create/Edit Modal -->
    <CreateModal
      v-model="showModal"
      :title="isEditing ? 'Edit Test' : 'Add Test'"
      icon="check-square"
      size="lg"
      :loading="saving"
      :submit-text="isEditing ? 'Update' : 'Create'"
      :submit-disabled="!form.title || !form.duration_minutes"
      :item-id="isEditing ? editingId : ''"
      @submit="saveTest"
    >
      <form @submit.prevent="saveTest" class="modal-form">
        <div class="form-group">
          <label class="form-label">Test Title <span class="text-danger">*</span></label>
          <input 
            v-model="form.title" 
            type="text" 
            class="form-control" 
            placeholder="e.g., Chapter 1 Quiz"
            required
          />
        </div>

        <div class="form-group">
          <label class="form-label">Description</label>
          <textarea 
            v-model="form.description" 
            class="form-control" 
            rows="2"
            placeholder="Brief description of the test..."
          ></textarea>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="form-label">Duration (minutes) <span class="text-danger">*</span></label>
            <input 
              v-model.number="form.duration_minutes" 
              type="number" 
              class="form-control" 
              placeholder="30"
              min="1"
              required
            />
          </div>

          <div class="form-group">
            <label class="form-label">Total Marks</label>
            <input 
              v-model.number="form.total_marks" 
              type="number" 
              class="form-control" 
              placeholder="100"
              min="1"
            />
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="form-label">Passing Marks</label>
            <input 
              v-model.number="form.passing_marks" 
              type="number" 
              class="form-control" 
              placeholder="40"
              min="0"
            />
          </div>

          <div class="form-group">
            <label class="form-label">Negative Marking</label>
            <input 
              v-model.number="form.negative_marking" 
              type="number" 
              class="form-control" 
              placeholder="0.25"
              step="0.01"
              min="0"
            />
          </div>
        </div>

        <div class="form-group">
          <label class="form-label">Status</label>
          <select v-model="form.status" class="form-control">
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>
        </div>
      </form>
    </CreateModal>

    <!-- Populate Questions Modal -->
    <PopulateQuestionsModal
      v-model="showPopulateModal"
      :test-id="populateTestId"
      :parent-type="parentType"
      :parent-id="parentId"
      :parent-name="parentName"
      @success="onPopulateSuccess"
    />
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import api from '@/services/api'
import { useAlert } from '@/plugins/sweetalert'
import DataTable from '@/components/data-table/DataTable.vue'
import CreateModal from '@/components/modals/CreateModal.vue'
import PopulateQuestionsModal from '@/components/modals/PopulateQuestionsModal.vue'

export default {
  name: 'TestsList',
  components: { DataTable, CreateModal, PopulateQuestionsModal },
  setup() {
    const route = useRoute()
    const alert = useAlert()
    
    const lessonId = route.params.lessonId
    const seriesId = route.params.seriesId
    const topicId = route.params.topicId
    const subjectId = ref(null) // Will be populated from parent data
    const parentType = lessonId ? 'lesson' : seriesId ? 'series' : 'topic'

    const parentName = ref('')
    const parentData = ref(null)
    const tests = ref([])
    const loading = ref(true)

    // Modal state
    const showModal = ref(false)
    const isEditing = ref(false)
    const saving = ref(false)
    const editingId = ref(null)
    
    // Populate modal state
    const showPopulateModal = ref(false)
    const populateTestId = ref('')
    
    const form = reactive({
      title: '',
      description: '',
      duration_minutes: 30,
      total_marks: 100,
      passing_marks: 40,
      negative_marking: 0,
      status: 'draft'
    })

    const columns = [
      { key: 'title', label: 'Test Title', sortable: true, minWidth: '250px' },
      { key: 'duration', label: 'Duration', sortable: true },
      { key: 'questions', label: 'Questions', class: 'text-center' },
      { key: 'marks', label: 'Marks', sortable: true },
      { key: 'status', label: 'Status', sortable: true },
      { key: 'actions', label: 'Actions', class: 'text-end' }
    ]

    const backUrl = computed(() => {
      if (parentType === 'lesson') return `/app/lessons/edit/${lessonId}`
      if (parentType === 'series') return `/app/test-series/edit/${seriesId}`
      return `/app/topics/edit/${topicId}`
    })

    const parentId = computed(() => {
      if (parentType === 'lesson') return lessonId
      if (parentType === 'series') return seriesId
      return topicId
    })

    const breadcrumbs = computed(() => {
      const items = [
        { title: 'Dashboard', url: '/app/dashboard' },
        { title: 'Exams', url: '/app/exams/list' }
      ]
      
      if (parentType === 'lesson') {
        // Exam → Subject → Topic → Lesson → Tests
        if (parentData.value?.examId) {
          items.push({ title: parentData.value?.examName || 'Exam', url: `/app/exams/${parentData.value.examId}/subjects` })
        }
        if (parentData.value?.subjectId) {
          items.push({ title: parentData.value?.subjectName || 'Subject', url: `/app/subjects/${parentData.value.subjectId}/topics` })
        }
        if (parentData.value?.topicId) {
          items.push({ title: parentData.value?.topicName || 'Topic', url: `/app/topics/${parentData.value.topicId}/lessons` })
        }
        items.push({ title: parentName.value || 'Lesson', active: true })
      } else if (parentType === 'series') {
        // Exam → TestSeries → Tests
        if (parentData.value?.examId) {
          items.push({ title: parentData.value?.examName || 'Exam', url: `/app/exams/${parentData.value.examId}/test-series` })
        }
        items.push({ title: parentName.value || 'Test Series', active: true })
      } else {
        // Exam → Subject → Topic → Tests (topic-level tests)
        if (parentData.value?.examId) {
          items.push({ title: parentData.value?.examName || 'Exam', url: `/app/exams/${parentData.value.examId}/subjects` })
        }
        if (subjectId.value) {
          items.push({ title: parentData.value?.subjectName || 'Subject', url: `/app/subjects/${subjectId.value}/topics` })
        }
        items.push({ title: parentName.value || 'Topic', active: true })
      }
      return items
    })

    const fetchData = async () => {
      loading.value = true
      try {
        let endpoint = ''
        if (parentType === 'lesson') {
          endpoint = `/v1/admin/lessons/${lessonId}/tests`
        } else if (parentType === 'series') {
          endpoint = `/v1/admin/testseries/${seriesId}/tests`
        } else {
          endpoint = `/v1/admin/topics/${topicId}/tests`
        }
        
        const testsRes = await api.get(endpoint)
        tests.value = testsRes.data.tests || []

        if (parentType === 'lesson') {
          const parentRes = await api.get(`/v1/admin/lessons/${lessonId}`)
          parentName.value = parentRes.data.lesson?.title || ''
          parentData.value = parentRes.data.lesson || {}
        } else if (parentType === 'series') {
          const parentRes = await api.get(`/v1/admin/testseries/${seriesId}`)
          parentName.value = parentRes.data.testSeries?.name || ''
          parentData.value = parentRes.data.testSeries || {}
        } else {
          const parentRes = await api.get(`/v1/admin/topics/${topicId}`)
          parentName.value = parentRes.data.topic?.name || ''
          parentData.value = parentRes.data.topic || {}
          subjectId.value = parentRes.data.topic?.subject_id || parentRes.data.topic?.subjectId || null
        }
      } catch (err) {
        console.error('Failed to fetch data:', err)
      } finally {
        loading.value = false
      }
    }

    const resetForm = () => {
      form.title = ''
      form.description = ''
      form.duration_minutes = 30
      form.total_marks = 100
      form.passing_marks = 40
      form.negative_marking = 0
      form.status = 'draft'
      editingId.value = null
      isEditing.value = false
    }

    const openCreateModal = () => {
      resetForm()
      showModal.value = true
    }

    const openPopulateModal = (test) => {
      populateTestId.value = test.id
      showPopulateModal.value = true
    }

    const onPopulateSuccess = () => {
      alert.success('Success!', 'Questions have been generated and added to the test.')
      fetchData()
    }

    const openEditModal = (test) => {
      isEditing.value = true
      editingId.value = test.id
      form.title = test.title || ''
      form.description = test.description || ''
      form.duration_minutes = test.durationMinutes || test.duration || test.duration_minutes || 30
      form.total_marks = test.totalMarks || test.total_marks || 100
      form.passing_marks = test.passingMarks || test.passing_marks || 40
      form.negative_marking = test.negativeMarking || test.negative_marking || 0
      form.status = test.status || 'draft'
      showModal.value = true
    }

    const saveTest = async () => {
      if (!form.title || !form.duration_minutes) return
      
      saving.value = true
      try {
        const payload = { ...form }
        if (parentType === 'lesson') payload.lesson_id = lessonId
        else if (parentType === 'series') payload.series_id = seriesId
        else payload.topic_id = topicId

        if (isEditing.value) {
          const response = await api.put(`/v1/admin/tests/${editingId.value}`, payload)
          const index = tests.value.findIndex(t => t.id === editingId.value)
          if (index !== -1) {
            tests.value[index] = { ...tests.value[index], ...response.data.test || payload }
          }
          alert.success('Updated!', 'Test updated successfully')
        } else {
          const response = await api.post('/v1/admin/tests', payload)
          tests.value.push(response.data.test || response.data)
          alert.success('Created!', 'Test created successfully')
        }
        showModal.value = false
        resetForm()
      } catch (error) {
        alert.error('Error', error.response?.data?.error?.message || 'Failed to save test')
      } finally {
        saving.value = false
      }
    }

    const formatDate = (date) => new Date(date).toLocaleString()

    const getStatusClass = (status) => {
      const classes = {
        draft: 'status-inactive',
        published: 'status-active',
        archived: 'status-pending'
      }
      return classes[status] || 'status-inactive'
    }

    const confirmDelete = async (test) => {
      const result = await alert.delete(
        'Delete Test?',
        `This will also delete all questions and results for "${test.title}".`
      )

      if (result.isConfirmed) {
        try {
          await api.delete(`/v1/admin/tests/${test.id}`)
          tests.value = tests.value.filter(t => t.id !== test.id)
          alert.success('Deleted!', 'Test has been deleted.')
        } catch (err) {
          alert.error('Error', 'Failed to delete test')
        }
      }
    }

    onMounted(fetchData)

    return { 
      parentType, 
      parentName,
      parentId,
      tests, 
      loading,
      columns,
      breadcrumbs, 
      backUrl,
      showModal,
      showPopulateModal,
      populateTestId,
      isEditing,
      saving,
      form,
      openCreateModal,
      openPopulateModal,
      onPopulateSuccess,
      openEditModal,
      saveTest,
      formatDate, 
      getStatusClass, 
      confirmDelete 
    }
  }
}
</script>

<style scoped>
.modal-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  font-weight: 500;
  font-size: 0.875rem;
  color: #374151;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-control {
  padding: 0.625rem 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.btn-xs {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  line-height: 1;
  border-radius: 4px;
}

:global(body.dark-mode) .form-label {
  color: #d1d5db;
}

:global(body.dark-mode) .form-control {
  background: #1e293b;
  border-color: #374151;
  color: #f3f4f6;
}
</style>
